import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/auth-context";
import { Sparkles } from "lucide-react";

interface AuthConfig {
  emailPasswordEnabled: boolean;
  signUpEnabled: boolean;
  entraIdEnabled: boolean;
  entraIdTenantId: string | null;
}

function MicrosoftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

export function Auth() {
  const navigate = useNavigate();
  const { signIn, signInWithProvider, isAuthenticated } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [authConfig, setAuthConfig] = useState<AuthConfig | null>(null);

  // MCP OAuth resume: the Better Auth mcp plugin redirects unauthenticated
  // /api/auth/mcp/authorize requests here with the original OAuth query
  // (client_id, redirect_uri, PKCE…). Once a session exists we forward the
  // browser back to the authorize endpoint so the flow completes.
  const search = window.location.search.startsWith("?")
    ? window.location.search.slice(1)
    : window.location.search;
  const isMcpAuthorize =
    new URLSearchParams(window.location.search).has("client_id") &&
    new URLSearchParams(window.location.search).has("response_type");
  const resumeMcpAuthorize = () => {
    window.location.replace(`/api/auth/mcp/authorize?${search}`);
  };

  useEffect(() => {
    fetch("/api/admin/auth-config")
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => { if (data) setAuthConfig(data); })
      .catch(() => {});
  }, []);

  // Redirect if already logged in
  if (isAuthenticated) {
    if (isMcpAuthorize) {
      resumeMcpAuthorize();
      return null;
    }
    navigate("/dashboard", { replace: true });
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const result = await signIn(email, password);
      if (result.error) {
        setError(result.error);
      } else if (isMcpAuthorize) {
        resumeMcpAuthorize();
      } else {
        navigate("/dashboard", { replace: true });
      }
    } catch {
      setError("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoftSignIn = async () => {
    setError("");
    try {
      // For MCP authorize flows, land back on this page with the pending
      // OAuth query so the isAuthenticated branch forwards to authorize.
      await signInWithProvider(
        "microsoft-entra-id",
        isMcpAuthorize ? `${window.location.origin}/auth?${search}` : undefined,
      );
    } catch {
      setError("Failed to initiate Microsoft sign-in");
    }
  };

  const entraIdEnabled = authConfig?.entraIdEnabled ?? false;

  return (
    <div className="flex h-screen w-screen items-center justify-center bg-background">
      <Card className="w-full max-w-sm">
        <CardHeader className="items-center text-center">
          <div className="mb-2 flex items-center gap-2">
            <Sparkles size={24} className="text-accent" />
            <span className="text-2xl font-semibold text-text-primary">ProClaw</span>
          </div>
          <CardTitle className="text-lg">Sign In</CardTitle>
          <CardDescription>Enter your credentials to continue</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4">
            {/* Microsoft Entra ID SSO button */}
            {entraIdEnabled && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={handleMicrosoftSignIn}
                >
                  <MicrosoftIcon className="mr-2 h-4 w-4" />
                  Sign in with Microsoft
                </Button>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-border" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-text-muted">or</span>
                  </div>
                </div>
              </>
            )}

            {/* Email/password form */}
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <Input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <Input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
              />

              {error && (
                <p className="text-sm text-red-500">{error}</p>
              )}

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "Please wait..." : "Sign In"}
              </Button>
            </form>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
